import React, { useEffect, useState } from "react";
import axios from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSync,
  FaPlus,
  FaEdit,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

function RecetaProductoPage() {
  const [productos, setProductos] = useState([]);
  const [productoSeleccionadoId, setProductoSeleccionadoId] = useState("");
  const [productoSeleccionadoNombre, setProductoSeleccionadoNombre] =
    useState("");

  const [insumos, setInsumos] = useState([]);

  const [recetaItems, setRecetaItems] = useState([]);
  const [loadingReceta, setLoadingReceta] = useState(false);
  const [loading, setLoading] = useState(false);

  const [busquedaProducto, setBusquedaProducto] = useState("");

  useEffect(() => {
    cargarProductos();
    cargarInsumos();
  }, []);

  const cargarProductos = async () => {
    try {
      const res = await axios.get("/productos");
      setProductos(res.data || []);
    } catch (error) {
      console.error("Error al cargar productos:", error);
      toast.error("❌ Error al cargar productos");
    }
  };

  const cargarInsumos = async () => {
    try {
      const res = await axios.get("/insumos", { params: { activo: true } });
      setInsumos(res.data || []);
    } catch (error) {
      console.error("Error al cargar insumos:", error);
      toast.error("❌ Error al cargar insumos");
    }
  };

  const cargarReceta = async (productoId) => {
    if (!productoId) return;

    setLoadingReceta(true);
    try {
      const res = await axios.get(`/recetas/producto/${productoId}`);
      const data = res.data || {};

      setProductoSeleccionadoNombre(data.productoNombre || "");

      const items = (data.items || []).map((item) => ({
        id: item.id,
        insumoId: item.insumoId,
        cantidadPorUnidad: item.cantidadPorUnidad,
      }));

      setRecetaItems(items);
    } catch (error) {
      console.error("Error al cargar receta:", error);
      if (error.response && error.response.status === 404) {
        toast.info("ℹ️ El producto no tiene receta definida aún");
        setRecetaItems([]);
        const prod = productos.find((p) => p.id === Number(productoId));
        setProductoSeleccionadoNombre(prod?.nombre || "");
      } else {
        toast.error("❌ Error al cargar la receta");
      }
    } finally {
      setLoadingReceta(false);
    }
  };

  const handleProductoChange = (e) => {
    const id = e.target.value;
    setProductoSeleccionadoId(id);
    if (id) {
      cargarReceta(id);
    } else {
      setProductoSeleccionadoNombre("");
      setRecetaItems([]);
    }
  };

  const agregarFila = () => {
    setRecetaItems((prev) => [
      ...prev,
      {
        id: null,
        insumoId: "",
        cantidadPorUnidad: "",
      },
    ]);
  };

  const actualizarFila = (index, campo, valor) => {
    setRecetaItems((prev) =>
      prev.map((fila, i) =>
        i === index
          ? {
              ...fila,
              [campo]: valor,
            }
          : fila
      )
    );
  };

  const eliminarFila = (index) => {
    setRecetaItems((prev) => prev.filter((_, i) => i !== index));
  };

  const guardarReceta = async () => {
    if (!productoSeleccionadoId) {
      toast.warning("⚠️ Selecciona un producto primero");
      return;
    }

    const itemsPayload = recetaItems
      .map((fila) => ({
        insumoId: fila.insumoId,
        cantidadPorUnidad: Number(fila.cantidadPorUnidad),
      }))
      .filter(
        (f) => f.insumoId && !isNaN(f.cantidadPorUnidad) && f.cantidadPorUnidad > 0
      );

    if (itemsPayload.length === 0) {
      if (
        !window.confirm(
          "La receta quedará vacía (se eliminarán insumos anteriores). ¿Continuar?"
        )
      ) {
        return;
      }
    }

    setLoading(true);
    try {
      await axios.put(`/recetas/producto/${productoSeleccionadoId}`, {
        items: itemsPayload,
      });
      toast.success("✅ Receta guardada correctamente");
      cargarReceta(productoSeleccionadoId);
    } catch (error) {
      console.error("Error al guardar receta:", error);
      toast.error("❌ No se pudo guardar la receta");
    } finally {
      setLoading(false);
    }
  };

  const productosFiltrados = productos.filter((p) =>
    (p.nombre || "").toLowerCase().includes(busquedaProducto.toLowerCase())
  );

  const obtenerNombreInsumo = (insumoId) => {
    const ins = insumos.find((i) => i.id === Number(insumoId));
    if (!ins) return "";
    return `${ins.nombre} ${
      ins.unidadMedida ? `(${ins.unidadMedida})` : ""
    }`;
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 mt-3">
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300 flex items-center gap-2">
            <FaEdit className="text-amber-400" />
            Recetas de Productos
          </h2>

          <div className="flex flex-wrap items-center gap-2">
            {/* Buscador de producto */}
            <div className="flex items-center rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 overflow-hidden">
              <span className="pl-2 pr-1 text-slate-400">
                <FaSearch className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                placeholder="Buscar producto..."
                value={busquedaProducto}
                onChange={(e) => setBusquedaProducto(e.target.value)}
                className="px-2 py-1.5 text-xs sm:text-sm bg-transparent text-slate-800 dark:text-slate-100 focus:outline-none"
                style={{ width: "170px" }}
              />
              {busquedaProducto && (
                <button
                  type="button"
                  onClick={() => setBusquedaProducto("")}
                  className="px-2 py-1.5 text-xs text-slate-400 hover:text-red-400"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Select de productos */}
            <select
              className="px-3 py-1.5 text-xs sm:text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={productoSeleccionadoId}
              onChange={handleProductoChange}
              style={{ minWidth: "200px" }}
            >
              <option value="">Selecciona un producto</option>
              {productosFiltrados.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>

            <button
              onClick={() => {
                if (productoSeleccionadoId) {
                  cargarReceta(productoSeleccionadoId);
                }
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <FaSync className={loadingReceta ? "animate-spin text-sky-500" : "text-sky-500"} />
              Recargar
            </button>

            <button
              onClick={agregarFila}
              disabled={!productoSeleccionadoId}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-white transition-colors ${
                productoSeleccionadoId
                  ? "bg-emerald-500 hover:bg-emerald-600"
                  : "bg-emerald-500/40 cursor-not-allowed"
              }`}
            >
              <FaPlus />
              Agregar insumo
            </button>
          </div>
        </div>

        {/* Info producto seleccionado */}
        <div className="mb-3 flex justify-between items-center">
          <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {productoSeleccionadoId
              ? `Editando receta de: ${productoSeleccionadoNombre || "Producto"}`
              : "Selecciona un producto para configurar su receta."}
          </span>

          <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            {recetaItems.length > 0
              ? `La receta tiene ${recetaItems.length} insumo${
                  recetaItems.length !== 1 ? "s" : ""
                }`
              : productoSeleccionadoId
              ? "La receta está vacía (sin insumos)"
              : ""}
          </span>
        </div>

        {/* Tabla de receta */}
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[800px] md:min-w-full text-sm">
              <thead className="bg-slate-100 dark:bg-slate-950/80">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    #
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Insumo
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Cantidad por unidad
                  </th>
                  <th className="px-4 py-2 text-left font-semibold text-slate-700 dark:text-slate-100">
                    Unidad
                  </th>
                  <th className="px-4 py-2 text-center font-semibold text-slate-700 dark:text-slate-100">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {!productoSeleccionadoId ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                    >
                      Selecciona un producto para ver o definir su receta.
                    </td>
                  </tr>
                ) : recetaItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-4 text-center text-slate-500 dark:text-slate-400 text-sm"
                    >
                      {loadingReceta
                        ? "Cargando receta..."
                        : "Este producto no tiene insumos en la receta todavía."}
                    </td>
                  </tr>
                ) : (
                  recetaItems.map((fila, index) => {
                    const ins = insumos.find(
                      (i) => i.id === Number(fila.insumoId)
                    );
                    return (
                      <tr
                        key={index}
                        className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <td className="px-4 py-2 text-left align-middle">
                          {index + 1}
                        </td>
                        <td className="px-4 py-2 text-left align-middle">
                          <select
                            value={fila.insumoId}
                            onChange={(e) =>
                              actualizarFila(index, "insumoId", e.target.value)
                            }
                            className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-2 py-1.5 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                          >
                            <option value="">Selecciona un insumo</option>
                            {insumos.map((ins) => (
                              <option key={ins.id} value={ins.id}>
                                {ins.nombre}{" "}
                                {ins.unidadMedida && `(${ins.unidadMedida})`}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={fila.cantidadPorUnidad}
                            onChange={(e) =>
                              actualizarFila(
                                index,
                                "cantidadPorUnidad",
                                e.target.value
                              )
                            }
                            className="w-28 mx-auto rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900/70 px-2 py-1.5 text-xs sm:text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent text-center"
                          />
                        </td>
                        <td className="px-4 py-2 text-left align-middle">
                          <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                            {ins?.unidadMedida || "-"}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center align-middle">
                          <button
                            onClick={() => eliminarFila(index)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-rose-500 text-rose-300 hover:bg-rose-500 hover:text-slate-900 transition-colors"
                          >
                            <FaTimes />
                            Quitar
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer - botón guardar */}
        <div className="flex justify-end mt-4">
          <button
            onClick={guardarReceta}
            disabled={!productoSeleccionadoId || loading}
            className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-medium text-white transition-colors inline-flex items-center gap-2 ${
              !productoSeleccionadoId || loading
                ? "bg-emerald-500/40 cursor-not-allowed"
                : "bg-emerald-500 hover:bg-emerald-600"
            }`}
          >
            <FaEdit />
            {loading ? "Guardando..." : "Guardar Receta"}
          </button>
        </div>
      </div>
    </>
  );
}

export default RecetaProductoPage;
